import { z } from "zod";
import i18n from "../i18n";
import { STOReportsScreen } from "../screens/reports/sto/STOReportsScreen";
import { HotelReportsScreen } from "../screens/reports/hotel/HotelReportsScreen";
import { Field } from "../config/companies/config";

export const createZodSchema = (fields: Field[]) => {
  const { t } = i18n;
  const schemaShape = fields.reduce((shape: { [key: string]: any }, field) => {
    let fieldSchema;

    switch (field.type) {
      case "phone":
        fieldSchema = z.string().trim();
        if (field.required) {
          fieldSchema = fieldSchema.min(
            1,
            t("Field is required", { field: field.label }),
          );
        }
        break;
      case "text":
      case "textarea":
      case "password":
        fieldSchema = z.string().trim();
        if (field.required) {
          fieldSchema = fieldSchema.min(
            1,
            t("Field is required", { field: field.label }),
          );
        }
        break;
      case "email":
        fieldSchema = z.email(t("Invalid email address"));
        if (field.required) {
          fieldSchema = fieldSchema.min(
            1,
            t("Field is required", { field: field.label }),
          );
        }
        break;
      case "number":
        if (field.required) {
          fieldSchema = z.number().nonnegative();
        } else {
          fieldSchema = z.number().nonnegative().optional();
        }
        break;
      case "dropdown":
        fieldSchema = field.multiselect ? z.array(z.string()) : z.string();
        if (field.required) {
          fieldSchema = field.multiselect
            ? fieldSchema.min(
                1,
                t("Must select at least one option", { field: field.label }),
              )
            : fieldSchema.min(
                1,
                t("Field is required", { field: field.label }),
              );
        }
        break;
      case "collectionPicker":
        fieldSchema = z.any();
        if (field.required) {
          fieldSchema = fieldSchema.refine(
            (value) => (Array.isArray(value) ? value.length > 0 : !!value),
            { message: t("Field is required", { field: field.label }) },
          );
        }
        break;
      case "booleanCheckbox":
        fieldSchema = z.boolean();
        break;
      case "datePicker":
        fieldSchema = field.required
          ? z.date()
          : z.date().optional().nullable();
        break;
      default:
        fieldSchema = z.any();
    }

    if (field.required) {
      fieldSchema = fieldSchema.refine(
        (val) => val !== null && val !== undefined,
        {
          message: t("Field is required", { field: field.label }),
        },
      );
    }

    shape[field.name] = fieldSchema;
    return shape;
  }, {});

  return z.object(schemaShape);
};

function hasUndefined(obj: any, path: string[] = []): boolean {
  for (const key in obj) {
    const value = obj[key];
    const currentPath = [...path, key];

    if (value === undefined) {
      alert(`Error: Field "${currentPath.join(".")}" is undefined.`);
      return true;
    }

    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      if (hasUndefined(value, currentPath)) {
        return true;
      }
    }
  }
  return false;
}

export function validateFirestoreData(data: any): boolean {
  const hasError = hasUndefined(data);
  if (!hasError) {
    return true; // Everything is good
  }
  return false; // There's at least one undefined field
}

const reportScreens: { [key: string]: any } = {
  ["STOReportsScreen"]: STOReportsScreen,
  ["HotelReportsScreen"]: HotelReportsScreen,
};

export const getReportsComponent = (screen: string) => {
  if (reportScreens[screen]) {
    return reportScreens[screen];
  }
};

export function getStatusAt(newStatus: string) {
  return "st_" + newStatus + "_at";
}

export function getPWADisplayMode() {
  // Check if we're in a web environment
  if (typeof window !== "undefined" && window.matchMedia) {
    if (window.matchMedia("(display-mode: browser)")?.matches) return "browser";
    if (window.matchMedia("(display-mode: standalone)")?.matches) return "pwa";
  }

  // For React Native or other environments, return a default value
  return "native";
}

export function safeToString(value: any): string {
  if (value === null || value === undefined) return "";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  )
    return String(value);
  if (typeof value === "object") {
    if ("name" in value && typeof value.name === "string") return value.name;
    if ("id" in value && typeof value.id === "string") return value.id;
    return JSON.stringify(value);
  }
  return String(value);
}

export function getNestedValue(obj: any, path: string): any {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
}

/**
 * Format a Date or Firestore Timestamp to "HH:mm:ss dd.MM" (no year)
 * Accepts either a Date or an object with `toDate()` (Firestore Timestamp).
 */
export function formatDateTimeNoYear(value: any): string {
  if (!value) return "";
  const d: Date =
    value && typeof value.toDate === "function"
      ? value.toDate()
      : new Date(value);
  const time = d.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${time} ${day}.${month}`;
}
